<template>
  <div class="loading-container">
    <!-- Background hexagon pattern -->
    <div class="hexagon-pattern">
      <div v-for="i in 6" :key="i" class="hexagon" :style="{ '--index': i }" />
    </div>

    <!-- Circular path container -->
    <div class="animation-wrapper">
      <div class="circular-path">
        <div class="bee-container">
          <div class="bee">
            <div class="body">
              <div class="line" />
            </div>
            <div>
              <div class="wing-right" />
              <div class="wing-left" />
            </div>
          </div>
        </div>
      </div>

      <!-- Loading text -->
      <div class="loading-text">{{ message || $t("platform.loading") }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
defineProps<{
  message?: string;
}>();
</script>

<style scoped>
.loading-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
  background: linear-gradient(
    135deg,
    #ff6b35 0%,
    #ff8c42 25%,
    #ffb549 50%,
    #ffd23f 75%,
    #ffeb3b 100%
  );
  overflow: hidden;
}

/* Hexagon pattern background */
.hexagon-pattern {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  pointer-events: none;
}

.hexagon {
  position: absolute;
  width: 200px;
  height: 200px;
  background: #000;
  clip-path: polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%);
  animation: float 20s ease-in-out infinite;
  animation-delay: calc(var(--index) * 2s);
}

.hexagon:nth-child(1) {
  top: 10%;
  left: 5%;
  transform: rotate(15deg);
}
.hexagon:nth-child(2) {
  top: 20%;
  right: 10%;
  transform: rotate(-30deg);
}
.hexagon:nth-child(3) {
  bottom: 30%;
  left: 15%;
  transform: rotate(45deg);
}
.hexagon:nth-child(4) {
  bottom: 15%;
  right: 20%;
  transform: rotate(-60deg);
}
.hexagon:nth-child(5) {
  top: 50%;
  left: 40%;
  transform: rotate(75deg);
}
.hexagon:nth-child(6) {
  top: 70%;
  right: 35%;
  transform: rotate(-90deg);
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) rotate(var(--rotation, 0deg));
  }
  50% {
    transform: translateY(-20px) rotate(calc(var(--rotation, 0deg) + 10deg));
  }
}

/* Animation wrapper */
.animation-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

/* Circular path for bee animation */
.circular-path {
  position: relative;
  width: 150px;
  height: 150px;
  animation: rotate 4s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Bee container to counter-rotate */
.bee-container {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  animation: counter-rotate 4s linear infinite;
}

@keyframes counter-rotate {
  from {
    transform: translateX(-50%) rotate(0deg);
  }
  to {
    transform: translateX(-50%) rotate(-360deg);
  }
}

/* Bee styling */
.bee {
  position: relative;
  width: 50px;
  height: 50px;
}

.body {
  position: relative;
  width: 45px;
  height: 25px;
  border: 4px solid #fbc02d;
  background: #ffeb3b;
  border-radius: 20px;
  perspective: 2500px;
  z-index: 99;
}

.body:before,
.body:after {
  content: "";
  position: absolute;
  width: 0;
  height: 0;
}

.body:before {
  right: 100%;
  top: 3px;
  border-top: 6px solid transparent;
  border-right: 13px solid #fbc02d;
  border-bottom: 6px solid transparent;
}

.body:after {
  right: 99%;
  top: 6px;
  border-top: 3px solid transparent;
  border-right: 6px solid #ffeb3b;
  border-bottom: 3px solid transparent;
}

.body .line {
  position: relative;
  left: 9px;
  height: 18px;
  width: 17px;
  background: #fbc02d;
}

.wing-right,
.wing-left {
  position: absolute;
  top: -17px;
  left: 12px;
  width: 23px;
  height: 23px;
  background: rgba(255, 255, 255, 0.8);
  border: 3px solid #fbc02d;
  border-radius: 100%;
}

.wing-right {
  left: 15px;
  transform: skew(-20deg);
  animation: wing-beat-right 0.15s infinite;
  transform-origin: bottom;
  z-index: 9;
}

.wing-left {
  transform: skew(20deg);
  animation: wing-beat-left 0.15s infinite;
  transform-origin: bottom;
  z-index: 999;
}

@keyframes wing-beat-right {
  50% {
    transform: rotateX(60deg) skew(-20deg) rotateZ(25deg);
  }
  100% {
    transform: rotateX(0) skew(-20deg);
  }
}

@keyframes wing-beat-left {
  50% {
    transform: rotateX(-65deg) skew(20deg) rotateZ(-10deg);
  }
  100% {
    transform: rotateX(0) skew(20deg);
  }
}

/* Loading text */
.loading-text {
  margin-top: 40px;
  font-size: 1.5rem;
  font-weight: 500;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
